import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, tap, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { config } from '../../../app/config';

export type CurrentUser = {
  id: number;
  username: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  role?: string;
  roleId?: number | string;
  athleteId?: number;
};

type LoginResponseData = {
  access_token?: string;
  accessToken?: string;
  token?: string;
  user?: CurrentUser;
};

type LoginResponse = {
  access_token?: string;
  accessToken?: string;
  token?: string;
  user?: CurrentUser;
  data?: LoginResponseData;
};

@Injectable({ providedIn: 'root' })
export class AuthService {
  private apiUrl = config.apiAuthUrl;
  private readonly TOKEN_KEY = 'token';

  private userSubject = new BehaviorSubject<CurrentUser | null>(null);
  user$ = this.userSubject.asObservable();

  constructor(private http: HttpClient) {}

  login(credentials: { username: string; password: string }): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${this.apiUrl}/login`, credentials).pipe(
      tap((res) => {
        const token = this.extractAccessToken(res);
        if (token) {
          this.saveToken(token);
        }

        const user = this.extractUser(res);
        if (user) {
          const roleId = user.roleId != null ? Number(user.roleId) : null;
          const athleteId = user.athleteId != null ? Number(user.athleteId) : null;

          if (roleId === 1 || roleId === 3) localStorage.setItem('roleId', String(roleId));
          else localStorage.removeItem('roleId');

          if (athleteId != null && !Number.isNaN(athleteId)) localStorage.setItem('athleteId', String(athleteId));
          else localStorage.removeItem('athleteId');

          this.userSubject.next({
            ...user,
            roleId: roleId ?? undefined,
            athleteId: athleteId ?? undefined,
            fullName: user.fullName ?? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim(),
          });
        }
      })
    );
  }

  loadMe(): Observable<CurrentUser | null> {
    return this.http.get<CurrentUser>(`${this.apiUrl}/me`).pipe(
      tap((u) => {
        const roleId = u.roleId != null ? Number(u.roleId) : null;
        const athleteId = u.athleteId != null ? Number(u.athleteId) : null;

        if (roleId === 1 || roleId === 3) localStorage.setItem('roleId', String(roleId));
        else localStorage.removeItem('roleId');

        if (athleteId != null && !Number.isNaN(athleteId)) localStorage.setItem('athleteId', String(athleteId));
        else localStorage.removeItem('athleteId');

        this.userSubject.next({
          ...u,
          roleId: roleId ?? undefined,
          athleteId: athleteId ?? undefined,
          fullName: u.fullName ?? `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim(),
        });
      }),
      catchError(() => {
        this.userSubject.next(null);
        return of(null);
      })
    );
  }

  private extractAccessToken(res: LoginResponse): string | null {
    return (
      res?.access_token ??
      res?.accessToken ??
      res?.token ??
      res?.data?.access_token ??
      res?.data?.accessToken ??
      res?.data?.token ??
      null
    );
  }

  private extractUser(res: LoginResponse): CurrentUser | null {
    if (res?.user) return res.user;
    return res?.data?.user ?? null;
  }

 saveToken(token: string): void {
  try {
    sessionStorage.setItem(this.TOKEN_KEY, token);
  } catch (e) {
    console.error('sessionStorage token set failed:', e);
  }

  // ✅ مهم: توکن را از localStorage پاک کن تا بعد از بستن مرورگر وارد نشه
  try {
    localStorage.removeItem(this.TOKEN_KEY);
  } catch {}
}


getToken(): string | null {
  try {
    return sessionStorage.getItem(this.TOKEN_KEY) || null;
  } catch {
    return null;
  }
}


  isLoggedIn(): boolean {
    return !!this.getToken();
  }

  // ✅ FIX اصلی: JWT payload = Base64URL (نه Base64)
  private decodeJwtPayload(token: string): any | null {
    try {
      const part = token.split('.')[1];
      if (!part) return null;

      // base64url -> base64
      let base64 = part.replace(/-/g, '+').replace(/_/g, '/');

      // padding
      const pad = base64.length % 4;
      if (pad) base64 += '='.repeat(4 - pad);

      const json = atob(base64);
      return JSON.parse(json);
    } catch {
      return null;
    }
  }

  isTokenValid(): boolean {
    const token = this.getToken();
    if (!token) return false;

    const payload = this.decodeJwtPayload(token);
    if (!payload) return false;

    // اگر exp نبود، معتبر فرض کن
    if (payload.exp == null) return true;

    const expSec = Number(payload.exp);
    if (Number.isNaN(expSec)) return false;

    return Date.now() < expSec * 1000;
  }

  getRoleId(): number | null {
    const raw = localStorage.getItem('roleId') ?? sessionStorage.getItem('roleId');
    const n = Number(raw);
    return n === 1 || n === 3 ? n : null;
  }

  logout(): void {
    try {
      sessionStorage.removeItem(this.TOKEN_KEY);
    } catch {}
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem('roleId');
    localStorage.removeItem('athleteId');
    this.userSubject.next(null);
  }

  setUser(user: CurrentUser | null): void {
    this.userSubject.next(user);
  }
}
